import * as React from 'react';

export type TooltipPlacementProps = 'top' | 'bottom' | 'left' | 'right' | 'middle';

type TooltipProps = {
  children?: React.ReactNode;
  title?: React.ReactNode;
  placement?: TooltipPlacementProps;
};

const Tooltip = ({ children, title, placement = 'top' }: TooltipProps): React.JSX.Element => {
  const [isTooltipVisible, setTooltipVisible] = React.useState(false);

  const handleMouseEnter = (): void => {
    if (title) setTooltipVisible(true);
  };

  const handleMouseLeave = (): void => {
    setTooltipVisible(false);
  };

  // tap to toggle (for iPad / touch)
  const handleClick = (): void => {
    if (title) setTooltipVisible(prev => !prev);
  };

  const getPlacementClasses = (): string => {
    switch (placement) {
      case 'top':
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
      case 'bottom':
        return 'top-full left-1/2 -translate-x-1/2 mt-2';
      case 'left':
        return 'right-full top-1/2 -translate-y-1/2 mr-2';
      case 'right':
        return 'left-full top-1/2 -translate-y-1/2 ml-2';
      case 'middle':
        return 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2';
      default:
        return 'bottom-full left-1/2 -translate-x-1/2 mb-2';
    }
  };

  const getArrowClasses = (): string => {
    const base = 'absolute w-0 h-0';

    switch (placement) {
      case 'top':
        return `${base} top-full left-1/2 -translate-x-1/2 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white drop-shadow-sm`;
      case 'bottom':
        return `${base} bottom-full left-1/2 -translate-x-1/2 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-white drop-shadow-sm`;
      case 'left':
        return `${base} left-full top-1/2 -translate-y-1/2 border-t-[6px] border-b-[6px] border-l-[6px] border-t-transparent border-b-transparent border-l-white drop-shadow-sm`;
      case 'right':
        return `${base} right-full top-1/2 -translate-y-1/2 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-white drop-shadow-sm`;
      case 'middle':
        return `${base} hidden`;
      default:
        return `${base} top-full left-1/2 -translate-x-1/2 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-white drop-shadow-sm`;
    }
  };

  return (
    <div
      className='relative inline-block'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      {children}

      {title && (
        <div
          className={`pointer-events-none absolute z-[9999] whitespace-nowrap break-words text-gray-600 bg-white py-2 px-3 text-xs font-medium rounded shadow-lg border text-center transition-opacity duration-150 ${
            isTooltipVisible ? 'opacity-100 visible' : 'opacity-0 invisible'
          } ${getPlacementClasses()}`}
        >
          {title}
          <div className={getArrowClasses()} />
        </div>
      )}
    </div>
  );
};

export default Tooltip;
